import { observer } from 'mobx-react-lite';
import { useState, type Ref } from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';
import cloudSkyPhoto from '@/assets/images/message-image/cloud-sky.webp';
import hillsidePhoto from '@/assets/images/message-image/hillside.webp';
import leafShadePhoto from '@/assets/images/message-image/leaf-shade.webp';
import windowMeadowPhoto from '@/assets/images/message-image/window-meadow.webp';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Markdown, StaticExportContext } from '@/components/ui/smoothdown';
import { cn } from '@/lib/utils';
import { userStore } from '@/stores/user.store';

type MessageImageCardProps = {
  ref: Ref<HTMLDivElement>;
  messageId: string;
  text: string;
};

type BackgroundPhoto = {
  src: string;
  scrimRgb: string;
};

const BACKGROUND_PHOTOS: BackgroundPhoto[] = [
  { src: leafShadePhoto, scrimRgb: '40 53 29' },
  { src: hillsidePhoto, scrimRgb: '32 56 26' },
  { src: cloudSkyPhoto, scrimRgb: '7 59 75' },
  { src: windowMeadowPhoto, scrimRgb: '47 50 31' },
];

const MAX_TEXT_LINES = 200;
const MAX_TEXT_CHARS = 12_000;

const boundText = (text: string) => {
  const boundedText = text
    .split('\n', MAX_TEXT_LINES)
    .join('\n')
    .slice(0, MAX_TEXT_CHARS);
  return { boundedText, isCut: boundedText.length < text.length };
};

const pickBackgroundPhoto = () =>
  BACKGROUND_PHOTOS[Math.floor(Math.random() * BACKGROUND_PHOTOS.length)];

const photoBackground = ({ src, scrimRgb }: BackgroundPhoto) =>
  `linear-gradient(to top, rgb(${scrimRgb} / 0.75), rgb(${scrimRgb} / 0.3) 5rem, transparent 10rem), url(${src})`;

export const MessageImageCard = observer(
  ({ ref, messageId, text }: MessageImageCardProps) => {
    const user = userStore.user;
    const [backgroundPhoto] = useState(pickBackgroundPhoto);
    const { boundedText, isCut } = boundText(text);

    return (
      <YStack
        ref={ref}
        className="w-[42rem] min-h-[42rem] gap-8 p-8 bg-background-subtle dark:bg-background bg-cover bg-center text-foreground"
        style={{ backgroundImage: photoBackground(backgroundPhoto) }}
      >
        <YStack className="flex-1 justify-center">
          <YStack
            className={cn(
              'rounded-2xl bg-background px-7 py-6',
              'ring-[0.375rem] ring-white/50 dark:ring-grayA-3 shadow-xl'
            )}
          >
            <YStack
              data-clip-content
              data-static-export
              className="peer max-h-[154rem] overflow-hidden data-[clipped]:[mask-image:linear-gradient(to_bottom,black_calc(100%_-_8rem),transparent)]"
            >
              <StaticExportContext.Provider value>
                <Markdown
                  id={`message-image-${messageId}`}
                  content={boundedText}
                  enableCopyableBlocks
                  copyableBlocksEditable={false}
                />
              </StaticExportContext.Provider>
            </YStack>
            <span
              className={cn(
                'mt-2 hidden text-sm text-foreground-muted peer-data-[clipped]:block',
                isCut && 'block'
              )}
            >
              Continues in Littlebird
            </span>
          </YStack>
        </YStack>
        <XStack className="dark items-center text-foreground">
          {user?.name && (
            <XStack className="items-center gap-2 text-sm">
              <span>From</span>
              <XStack
                center
                className="relative size-6 shrink-0 overflow-hidden rounded-full bg-surface-active text-xs font-medium"
              >
                {user.name.charAt(0)}
                {user.avatar_url && (
                  <img
                    src={user.avatar_url}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="absolute inset-0 size-full object-cover"
                  />
                )}
              </XStack>
              <span>{user.name}</span>
            </XStack>
          )}
          <XStack className="ml-auto items-center gap-1.5">
            <AppLogo size={20} decorative />
            <span className="font-serif text-xl leading-none">Littlebird</span>
          </XStack>
        </XStack>
      </YStack>
    );
  }
);
